import { FC, useEffect, useState } from 'react';
import { Button, Card, Divider, RadixTable } from '@features/ui';
import {
  periodOptions,
  groupByOptions,
} from '@features/admin/reports/reportData';
import { toast } from 'react-toastify';
import {
  ReportPeriod,
  ReportGroupBy,
  TReportCreditsSpentByProductsBreakdownData,
  TReportCreditsSpentByProductsBreakdownParams,
} from '@src/types/reports';
import { TOption } from '@src/types';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import { useReportFilters } from '@hooks/useReportFilters';
import { useReportTableColumns } from '@hooks/useReportTableColumns';
import { useReportChartData, CHART_COLORS } from '@hooks/useReportChartData';
import dayjs from 'dayjs';
import {
  exportCreditsSpentByProductsBreakdown,
  getCreditsSpentByProductsBreakdown,
  getCreditsSpentByProductsBreakdownProducts,
} from '@src/services/reports';
import { downloadFile } from '@src/lib/utils';

import { ReportFilteringSettings } from '../ReportFilteringSettings';

interface TooltipProps {
  active?: boolean;
  payload?: Array<{
    value: number;
    dataKey: string;
    color: string;
    payload: any;
  }>;
}

const CustomTooltip: FC<TooltipProps> = ({ active, payload }) => {
  if (active && payload && payload.length) {
    return (
      <div
        className="bg-card-stat p-2.5 rounded-lg text-xs border border-[#3D3D3D] flex flex-col gap-y-1 min-w-[200px]"
        style={{ boxShadow: '0px 7px 13px 0px #B6D72C14' }}
      >
        <p className="text-[#999999] text-[10px] text-center mb-1">
          {payload[0]?.payload?.date}
        </p>
        {payload.map((entry, index) => (
          <div key={index} className="flex justify-between items-center gap-4">
            <span className="text-gray-300 text-xs">{entry.dataKey}:</span>
            <span
              className="text-white font-semibold"
              style={{ color: entry.color }}
            >
              {entry.value}
            </span>
          </div>
        ))}
      </div>
    );
  }

  return null;
};

export const CreditsSpentByProductsBreakdown: FC = () => {
  // Data state
  const [data, setData] = useState<
    TReportCreditsSpentByProductsBreakdownData[]
  >([]);
  const [products, setProducts] = useState<TOption[]>([]);

  // Use custom hooks for filter management
  const filters = useReportFilters({
    defaultPeriod: ReportPeriod.LAST_7_DAYS,
    defaultGroupBy: ReportGroupBy.DAY,
    enableProducts: true,
    enableComparisons: true,
  });

  // Use custom hook for table columns generation
  const { columns, displayItems, tableData } = useReportTableColumns({
    data,
    selectedItems: filters.selectedProducts,
    compareToPreviousPeriod: filters.compareToPreviousPeriod,
    compareToSamePeriodPreviousYear: filters.compareToSamePeriodPreviousYear,
    getAccessField: (item) => item.date,
    getMainDataField: (item, key) => item.products[key] || 0,
    getPreviousPeriodField: (item, key) => item.products_previous?.[key],
    getPreviousYearField: (item, key) => item.products_previous_year?.[key],
    itemsField: 'products',
    includeTotalsRow: true,
  });

  // Use custom hook for chart data transformation
  const chartData = useReportChartData({
    data,
    displayItems,
    compareToPreviousPeriod: filters.compareToPreviousPeriod,
    compareToSamePeriodPreviousYear: filters.compareToSamePeriodPreviousYear,
    getAccessField: (item) => item.date,
    getMainDataField: (item, key) => item.products[key] || 0,
    getPreviousPeriodField: (item, key) => item.products_previous?.[key],
    getPreviousYearField: (item, key) => item.products_previous_year?.[key],
  });

  const buildReportPayload =
    (): TReportCreditsSpentByProductsBreakdownParams => {
      const selectedProductIds = filters.selectedProducts
        .map((item) => Number(item))
        .filter((item) => !Number.isNaN(item));

      return {
        grouping: filters.selectedGroupBy as ReportGroupBy,
        start_date: filters.startDate
          ? dayjs(filters.startDate).format('YYYY-MM-DD')
          : null,
        end_date: filters.endDate
          ? dayjs(filters.endDate).format('YYYY-MM-DD')
          : null,
        product_ids: selectedProductIds.length ? selectedProductIds : undefined,
        compare_to_previous_period: filters.compareToPreviousPeriod,
        compare_to_same_period_previous_year:
          filters.compareToSamePeriodPreviousYear,
      };
    };

  const fetchProducts = async () => {
    try {
      const response = await getCreditsSpentByProductsBreakdownProducts();

      setProducts(
        response.map((item) => ({
          label: item.product_name,
          value: String(item.product_id),
        }))
      );
    } catch (error) {
      console.error('Error fetching product options:', error);
      toast.error('Failed to load product options');
    }
  };

  const handleRunReport = async () => {
    filters.setIsLoading(true);
    try {
      const payload = buildReportPayload();
      const response = await getCreditsSpentByProductsBreakdown(payload);

      setData(response.data);
      toast.success('Report generated successfully!');
    } catch (error) {
      toast.error('Error fetching report');
      console.error('Error fetching report:', error);
    } finally {
      filters.setIsLoading(false);
    }
  };

  const downloadCsvFile = (blobPart: BlobPart, fileName: string) => {
    const csvBlob = new Blob([blobPart], { type: 'text/csv;charset=utf-8;' });
    const csvUrl = URL.createObjectURL(csvBlob);
    const link = document.createElement('a');

    link.href = csvUrl;
    link.download = `${fileName}.csv`;
    document.body.appendChild(link);
    link.click();
    URL.revokeObjectURL(csvUrl);
    document.body.removeChild(link);
  };

  const handleExportReport = async (format: 'csv' | 'xlsx') => {
    try {
      const payload = buildReportPayload();
      const response = await exportCreditsSpentByProductsBreakdown(
        payload,
        format
      );

      if (format === 'csv') {
        downloadCsvFile(response.data, 'credits_spent_by_products_breakdown');
      } else {
        downloadFile(response.data, 'credits_spent_by_products_breakdown');
      }

      toast.success('Report exported successfully!');
    } catch (error) {
      console.error('Error exporting report:', error);
      toast.error('Failed to export report');
    }
  };

  useEffect(() => {
    fetchProducts();
    handleRunReport();
  }, []);

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-semibold text-white">
        Credits Spent by Products Breakdown
      </h1>

      <Card className="p-6">
        <ReportFilteringSettings
          compareToPreviousPeriod={filters.compareToPreviousPeriod}
          compareToSamePeriodPreviousYear={
            filters.compareToSamePeriodPreviousYear
          }
          dateRangeValue={filters.valueStart}
          groupByOptions={groupByOptions}
          isLoading={filters.isLoading}
          periodOptions={periodOptions}
          periodRange={filters.periodRange}
          productOptions={products}
          selectedGroupBy={filters.selectedGroupBy}
          selectedPeriod={filters.selectedPeriod}
          selectedProducts={filters.selectedProducts}
          showCompareToPreviousPeriod={filters.showPreviousPeriodCheckbox}
          showCompareToSamePeriodPreviousYear={filters.showPreviousYearCheckbox}
          showProductsFilter={true}
          onCompareToPreviousPeriodChange={filters.setCompareToPreviousPeriod}
          onCompareToSamePeriodPreviousYearChange={
            filters.setCompareToSamePeriodPreviousYear
          }
          onDateRangeChange={filters.setValueStart}
          onGroupByChange={filters.setSelectedGroupBy}
          onPeriodChange={filters.setSelectedPeriod}
          onProductsChange={filters.setSelectedProducts}
          onRunReport={handleRunReport}
        />
      </Card>

      <Card className="p-6">
        <div className="space-y-4">
          <ResponsiveContainer height={400} width="100%">
            <LineChart data={chartData}>
              <CartesianGrid stroke="#333" strokeDasharray="3 3" />
              <XAxis
                dataKey="date"
                stroke="#999"
                style={{ fontSize: '12px' }}
              />
              <YAxis
                stroke="#999"
                style={{ fontSize: '12px' }}
                tickFormatter={(value) => value.toLocaleString()}
              />
              <Tooltip content={<CustomTooltip />} />
              <Legend
                iconType="line"
                wrapperStyle={{ fontSize: '12px', paddingTop: '20px' }}
              />

              {/* Main data lines */}
              {displayItems.map((product, index) => (
                <Line
                  key={product}
                  activeDot={{ r: 6 }}
                  dataKey={product}
                  dot={{
                    fill: CHART_COLORS[index % CHART_COLORS.length],
                    r: 4,
                  }}
                  name={product}
                  stroke={CHART_COLORS[index % CHART_COLORS.length]}
                  strokeWidth={2}
                  type="monotone"
                />
              ))}

              {/* Previous period lines */}
              {filters.compareToPreviousPeriod &&
                displayItems.map((product, index) => (
                  <Line
                    key={`${product}_previous_period`}
                    activeDot={{ r: 4 }}
                    dataKey={`${product}_previous_period`}
                    dot={{
                      fill: CHART_COLORS[index % CHART_COLORS.length],
                      r: 3,
                    }}
                    name={`${product} (Previous Period)`}
                    stroke={CHART_COLORS[index % CHART_COLORS.length]}
                    strokeDasharray="5 5"
                    strokeWidth={1.5}
                    type="monotone"
                  />
                ))}

              {/* Previous year lines */}
              {filters.compareToSamePeriodPreviousYear &&
                displayItems.map((product, index) => (
                  <Line
                    key={`${product}_previous_year`}
                    activeDot={{ r: 4 }}
                    dataKey={`${product}_previous_year`}
                    dot={{
                      fill: CHART_COLORS[index % CHART_COLORS.length],
                      r: 3,
                    }}
                    name={`${product} (Previous Year)`}
                    stroke={CHART_COLORS[index % CHART_COLORS.length]}
                    strokeDasharray="2 2"
                    strokeWidth={1.5}
                    type="monotone"
                  />
                ))}
            </LineChart>
          </ResponsiveContainer>
        </div>
      </Card>

      <Card className="p-6">
        <div className="space-y-4">
          <h2 className="text-lg font-semibold text-white">Detailed Data</h2>
          <RadixTable columns={columns} data={tableData} />
          <Divider className="my-4" />
          <div className="flex justify-end">
            <Button
              className="text-[#B6D72C] border-[#B6D72C] hover:bg-[#B6D72C] hover:text-black"
              variant="outline"
              onClick={() => handleExportReport('csv')}
            >
              Download CSV
            </Button>
            <Button
              className="text-[#B6D72C] border-[#B6D72C] hover:bg-[#B6D72C] hover:text-black"
              variant="outline"
              onClick={() => handleExportReport('xlsx')}
            >
              Download Excel
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );
};
